import { useRef, useState, FC, PropsWithChildren, useCallback } from 'react';
import { Tooltip } from 'reactstrap';
import { ErrorBoundary } from '@/library/components/general/ErrorBoundary';
import type { Placement } from '@popperjs/core';
import useTooltipID from '@/library/utils/hooks/useTooltipID';

type TextWithTooltipProps = PropsWithChildren<{
  placement?: Placement;
  className?: string;
  innerTooltipClassName?: string;
  dataCy?: string;
  allowOverflow?: boolean;
  onClick?: () => void;
}>;

const TextWithTooltip: FC<TextWithTooltipProps> = ({
  children,
  placement = 'top',
  className = '',
  onClick = () => null,
  innerTooltipClassName = '',
  dataCy = undefined,
  allowOverflow = false
}) => {
  const parentElement = useRef<HTMLDivElement>(null);
  const overflownElement = useRef<HTMLDivElement>(null);

  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const { avoidRender, tooltipID } = useTooltipID('text-with-tooltip');

  const isOverflown = useCallback(() => {
    if (!overflownElement.current) return false;

    return overflownElement.current.scrollWidth > overflownElement.current.clientWidth;
  }, []);

  const handleMouseOver = useCallback(() => {
    if (!tooltipID || !document.getElementById(tooltipID)) {
      setShowTooltip(false);
      return;
    }

    const hasOpenDrawer = !!document
      .getElementById('drawer-container')
      ?.querySelector('.drawer.open');
    const isInsideOpenDrawer = parentElement.current?.closest('.drawer.open') !== null;

    // Suppress tooltips for content sitting behind open drawers;
    // allow them inside any open drawer (including sibling portals).
    if (hasOpenDrawer && !isInsideOpenDrawer) {
      setShowTooltip(false);
      return;
    }

    setShowTooltip(isOverflown());
  }, [isOverflown, tooltipID]);

  return (
    <div
      className={`text-overflow h-100p w-100p overflow-hidden ${className}`}
      ref={parentElement}
      onMouseOut={() => setShowTooltip(false)}
      onMouseOver={() => handleMouseOver()}
      data-testid="text-overflow"
    >
      <div
        onClick={() => {
          setShowTooltip(false);
          onClick();
        }}
        className={`text-overflow__text w-100p ${!allowOverflow ? 'text-truncate' : ''}`}
        ref={overflownElement}
        id={tooltipID}
        data-cy={dataCy}
      >
        {children}
      </div>
      {!avoidRender && !!tooltipID && showTooltip ? (
        <ErrorBoundary fallback={null} notifyBugsnag={false}>
          <Tooltip
            boundariesElement="window"
            arrowClassName="d-none"
            placement={placement}
            target={tooltipID}
            innerClassName={innerTooltipClassName}
            fade={false}
            isOpen={showTooltip}
          >
            {children}
          </Tooltip>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};

export default TextWithTooltip;
